import { Button, Label, Spinner, TextInput, Textarea } from 'flowbite-react';
import React, { useEffect } from 'react';
import { UseMutationResult } from 'react-query';
import { useParams } from 'react-router-dom';
import { MultiValue } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { Dropdown, validateEmailAddress, validateUrl } from 'shared-utils';
import GenerateMeetingLinkButton from 'src/components/shared/GenerateMeetingLinkButton';
import { ScheduleInterviewRoundInput } from 'types';

interface Props {
	scheduleInterViewRoundInput: ScheduleInterviewRoundInput;
	setScheduleInterViewRoundInput: React.Dispatch<React.SetStateAction<ScheduleInterviewRoundInput>>;
	scheduleInterviewFn: UseMutationResult<any, unknown, ScheduleInterviewRoundInput, unknown>;
	rescheduleInterviewFn: UseMutationResult<any, unknown, ScheduleInterviewRoundInput, unknown>;
	isReschedule?: boolean;
	disable?: boolean;
	isLoading?: boolean;
	onSubmit?: (data: ScheduleInterviewRoundInput) => void;
}

const InterviewForm = ({
	isReschedule,
	scheduleInterViewRoundInput,
	setScheduleInterViewRoundInput,
	scheduleInterviewFn,
	rescheduleInterviewFn,
	disable,
	isLoading,
	onSubmit,
}: Props) => {
	const { positionId } = useParams();
	const [inputValue, setInputValue] = React.useState('');
	const [value, setValue] = React.useState<MultiValue<Dropdown>>([]);
	const [emailErrorMessage, setEmailErrorMessage] = React.useState('');

	const handleInputChange = (newValue: string) => {
		if (!newValue) return;
		switch (newValue[newValue.length - 1]) {
			case ' ':
			case ',':
				// Check if inputValue already exists in the array
				if (validateEmailAddress(inputValue) && !value.some(item => item.value === inputValue)) {
					// If not, add it to the array
					setValue(prev => [...prev, { value: inputValue, label: inputValue }]);
					setScheduleInterViewRoundInput(prev => {
						return { ...prev, interviewers: [...value.map(val => val.value), inputValue] };
					});
				}
				if (!validateEmailAddress(inputValue)) setEmailErrorMessage('Please enter a valid email address');
				else setEmailErrorMessage('');
				setInputValue('');
				break;
		}
	};

	useEffect(() => {
		if (scheduleInterViewRoundInput?.interviewers?.length) {
			setValue(
				scheduleInterViewRoundInput?.interviewers?.map(interviewer => ({ value: interviewer, label: interviewer })),
			);
		}
	}, []);

	return (
		<div className="grid gap-[16px]">
			{/* Interview time (in mins) */}
			<div className="mb-2 block">
				<div className="mb-[5px]">
					<Label
						className="font-inter text-[14px] font-[400] text-[#28293D]"
						htmlFor="duration"
						value="Interview time (in mins)"
					/>
					<span className="text-[red]">*</span>
				</div>
				<TextInput
					name="duration"
					type="duration"
					value={isNaN(scheduleInterViewRoundInput.duration) ? '' : scheduleInterViewRoundInput.duration}
					placeholder="eg: 60"
					className="w-full [&_input]:bg-white"
					onChange={e => {
						if (e.target)
							setScheduleInterViewRoundInput({
								...scheduleInterViewRoundInput,
								duration: parseInt(e.target.value),
							});
					}}
				/>
			</div>
			{/* Meeting link * */}
			<div className="mb-2 block">
				<div className="mb-[5px] flex items-end justify-between">
					<div>
						<Label
							className="font-inter text-[14px] font-[400] text-[#28293D]"
							htmlFor="interviewLink"
							value="Meeting link"
						/>
					</div>

					<div>
						<GenerateMeetingLinkButton
							positionId={positionId || ''}
							onGenerate={meetingLink => {
								setScheduleInterViewRoundInput({ ...scheduleInterViewRoundInput, interviewLink: meetingLink });
							}}
						/>
					</div>
				</div>

				<TextInput
					name="interviewLink"
					type="interviewLink"
					value={scheduleInterViewRoundInput?.interviewLink}
					placeholder="Zoom / Google Meet/ Teams link"
					className="w-full [&_input]:bg-white"
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						setScheduleInterViewRoundInput({ ...scheduleInterViewRoundInput, interviewLink: e.target.value })
					}
				/>

				{scheduleInterViewRoundInput.interviewLink && !validateUrl(scheduleInterViewRoundInput.interviewLink) ? (
					<div className="mt-2 text-sm text-red-500">Please enter a valid url</div>
				) : null}
			</div>

			<div className="mb-2 block whiteBgSelect">
				<div className="mb-[5px]">
					<Label className="font-inter text-[14px] font-[400] text-[#28293D]">{'Add guest email '}</Label>
					{/* <span className="text-[red]">* </span> */}
					<span className="text-[12px] text-yellow-600">(Calendar invites will be sent)</span>{' '}
				</div>
				<div className="text-[10px] mb-[4px]">(Enter email separated by comma or space after each email id.)</div>
				<CreatableSelect
					components={{
						DropdownIndicator: null,
					}}
					inputValue={inputValue}
					isClearable
					isMulti
					menuIsOpen={false}
					onInputChange={newValue => {
						setInputValue(newValue);
						handleInputChange(newValue);
					}}
					onBlur={() => {
						if (inputValue) {
							handleInputChange(' ');
						}
					}}
					onChange={(vals: MultiValue<Dropdown>) => {
						setValue(vals);
						setScheduleInterViewRoundInput(prev => {
							return { ...prev, interviewers: vals.map(val => val.value) };
						});
					}}
					className="single-value-select-tag"
					classNamePrefix="my-select-plans"
					placeholder="teammate-name@company-name.com"
					value={value}
				/>

				{emailErrorMessage ? <div className="mt-2 text-sm text-red-500">{emailErrorMessage}</div> : null}
			</div>
			{/* Your message */}
			<div>
				<div className="mb-[8px] flex justify-between items-end">
					<div>
						<Label
							className="font-inter text-[14px] font-[400] text-[#28293D]"
							htmlFor="message"
							value="Your message"
						/>
						<span className="text-[red]">*</span>
					</div>

					<span className="italic text-xs text-[#6B7280] ">
						{(scheduleInterViewRoundInput?.message?.length || 0) + ' / ' + 640}
					</span>
				</div>
				<Textarea
					style={{
						fontFamily: "'Inter', sans-serif",
						borderRadius: '8px',
					}}
					value={scheduleInterViewRoundInput?.message}
					maxLength={640}
					id="message"
					name="message"
					placeholder="Write your message here..."
					className="border bg-white dark:text-white dark:border-[#4B5563] min-h-[87px] dark:bg-[#374151] focus:border-transparent focus:!ring-[1px] focus:!ring-[#7777E7]"
					onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
						setScheduleInterViewRoundInput({ ...scheduleInterViewRoundInput, message: e.target.value })
					}
				/>
			</div>
			<Button
				color="purplePlaced"
				disabled={
					isLoading ||
					scheduleInterviewFn.isLoading ||
					rescheduleInterviewFn.isLoading ||
					!scheduleInterViewRoundInput.duration ||
					(scheduleInterViewRoundInput.interviewLink && !validateUrl(scheduleInterViewRoundInput.interviewLink)) ||
					!scheduleInterViewRoundInput.message ||
					!scheduleInterViewRoundInput.startTime ||
					// scheduleInterViewRoundInput.interviewers.length === 0 ||
					disable
				}
				onClick={() => {
					if (onSubmit) {
						onSubmit(scheduleInterViewRoundInput);
					} else {
						isReschedule
							? rescheduleInterviewFn.mutate(scheduleInterViewRoundInput)
							: scheduleInterviewFn.mutate(scheduleInterViewRoundInput);
					}
				}}
			>
				{isLoading || scheduleInterviewFn.isLoading || rescheduleInterviewFn.isLoading ? (
					<Spinner />
				) : isReschedule ? (
					'Reschedule Interview'
				) : (
					'Schedule Interview'
				)}
			</Button>
		</div>
	);
};

export default InterviewForm;
